import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  applyCoupon,
  getCouponList,
  removeOneCoupon,
  clearPendingAppliedCoupon,
} from "../../../../../services/coupon/actions";
import BackWithSearch from "../../../Elements/BackWithSearch";
import classes from "./CouponList.module.css";
import Ink from "react-ink";
import { formatPrice } from "../../../../helpers/formatPrice";
import { getImageUrl } from '../../../../../helpers/imageUrl';
import { AppMode } from "../../../../../constants";

/**
 * Apply-coupon flow (from Cart, user taps "Apply Coupon" and lands here):
 * - Type code in the input and tap "APPLY" to apply by code, or
 * - Tap "APPLY" on a coupon card to apply that coupon. Both send subtotal to the API.
 */

/** Compute item total from cart products (same logic as total/actions + Cart). Ensures apply-coupon gets correct subtotal when state.total is 0. */
function getSubtotalFromProducts(products) {
  if (!products || !products.length) return 0;
  return products.reduce((sum, p) => {
    const qty = p.quantity || 1;
    if (p.selectedaddons && p.selectedaddons.length > 0) {
      let variationPrice = parseFloat(p.selectedaddons[0].price) || 0;
      if (p.selectedaddons.length > 1) {
        variationPrice = p.selectedaddons.reduce((addonSum, addonArray) => addonSum + parseFloat(addonArray.price || 0), 0);
      }
      return sum + variationPrice * qty;
    }
    return sum + (parseFloat(p.price) || 0) * qty;
  }, 0);
}

const CouponList = ({ history }) => {
  const dispatch = useDispatch();
  const [couponList, setCouponList] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState();
  const [enteredCoupon, setEnteredCoupon] = useState();
  const restaurantInfo = useSelector((state) => state.items && state.items.restaurant_info);
  const token = useSelector((state) => state.user && state.user.user && state.user.user.data && state.user.user.data.auth_token);
  const totalFromState = useSelector((state) => state.total && state.total.data && state.total.data.totalPrice ? parseFloat(state.total.data.totalPrice) : 0);
  const cartProducts = useSelector((state) => state.cart.products || []);
  const phase2CartProducts = useSelector((state) => state.cart.phase2Products || []);
  const appMode = useSelector((state) => state.settings && state.settings.appMode);
  // Applied coupons: slice shape is { coupon: { vendors, elevate, coupon }, ... }
  const appliedCouponState = useSelector((state) => (state.coupon && state.coupon.coupon) ? state.coupon.coupon : {});
  const appliedElevate = appliedCouponState.elevate || null;
  const appliedVendors = appliedCouponState.vendors || {};

  // Sprint: single store ID from cart or restaurant_info. Relay: all unique restaurant IDs from cart.
  const { storeId, storeIdsForApi } = useMemo(() => {
    const isRelay = appMode === AppMode.RELAY;
    if (isRelay && phase2CartProducts && phase2CartProducts.length > 0) {
      const ids = [];
      phase2CartProducts.forEach((p) => {
        const id = p.restaurant_id != null ? Number(p.restaurant_id) : null;
        if (id != null && !Number.isNaN(id) && ids.indexOf(id) === -1) ids.push(id);
      });
      return { storeId: ids[0] || null, storeIdsForApi: ids };
    }
    const single = (cartProducts && cartProducts[0] && cartProducts[0].restaurant_id != null)
      ? cartProducts[0].restaurant_id
      : (restaurantInfo && restaurantInfo.id);
    return { storeId: single != null ? Number(single) : null, storeIdsForApi: single != null ? [Number(single)] : [] };
  }, [appMode, phase2CartProducts, cartProducts, restaurantInfo]);

  const subtotal = useMemo(() => {
    if (totalFromState > 0) return totalFromState;
    const isRelay = appMode === AppMode.RELAY;
    const products = isRelay ? phase2CartProducts : cartProducts;
    return getSubtotalFromProducts(products);
  }, [totalFromState, appMode, phase2CartProducts, cartProducts]);

  useEffect(() => {
    const fetchCouponList = async () => {
      if (!token) {
        setCouponList([]);
        return;
      }
      const idParam = storeIdsForApi && storeIdsForApi.length > 1
        ? storeIdsForApi
        : (storeIdsForApi && storeIdsForApi[0]) || storeId;
      try {
        const result = await getCouponList(idParam || null, token);
        let list = (result && result.coupons && Array.isArray(result.coupons)) ? result.coupons : [];
        if (appliedElevate && appliedElevate.code) {
          const codeUpper = (appliedElevate.code || "").toString().toUpperCase();
          const alreadyInList = list.some((c) => (c.code || "").toString().toUpperCase() === codeUpper);
          if (!alreadyInList) {
            list = [{ ...appliedElevate, restaurant_id: appliedElevate.restaurant_id != null ? appliedElevate.restaurant_id : null }, ...list];
          }
        }
        setCouponList(list);
      } catch (error) {
        console.error("Error fetching data:", error);
        setCouponList([]);
      }
    };

    fetchCouponList();
  }, [token, storeId, (storeIdsForApi || []).join(","), appliedElevate ? appliedElevate.code : null]);

  const handleInput = (event) => {
    setEnteredCoupon(event.target.value.toUpperCase());
  };

  const getExpireDate = (incomingDate) => {
    const splitDate = incomingDate.split(" ");
    return splitDate[0];
  };

  const setCoupon = (coupon) => {
    if (!coupon || !token) {
      return;
    }
    // Vendor coupon: use that coupon's restaurant_id. Platform/universal: use first store ID for API.
    const restaurantId = (coupon.restaurant_id != null && coupon.restaurant_id !== "") ? Number(coupon.restaurant_id) : (storeId != null ? storeId : (storeIdsForApi && storeIdsForApi[0] ? storeIdsForApi[0] : null));
    if (restaurantId == null || restaurantId === "") {
      return;
    }
    if (Number(coupon.min_subtotal || 0) > subtotal) {
      return;
    }
    setSelectedCoupon(coupon);
    dispatch(applyCoupon(token, coupon.code, restaurantId, subtotal))
      .then((data) => {
        if (data && data.success) {
          try {
            sessionStorage.setItem("cart_applied_coupon_payload", JSON.stringify(data));
          } catch (e) { }
          history.push("/cart", { appliedCoupon: data });
        }
      })
      .catch(() => { });
  };

  const setAppliedCoupn = () => {
    if (enteredCoupon && enteredCoupon.trim().length && token && storeId) {
      dispatch(applyCoupon(token, enteredCoupon.trim(), storeId, subtotal))
        .then((data) => {
          if (data && data.success) {
            try {
              sessionStorage.setItem("cart_applied_coupon_payload", JSON.stringify(data));
            } catch (e) { }
            history.push("/cart", { appliedCoupon: data });
          }
        })
        .catch(() => { });
    }
  };

  const showMoreDetails = (index, couponCode) => {
    var showMore = document.getElementById(index);
    var showLess = document.getElementById(index + couponCode);
    if (showLess.style.display === "none" || showLess.style.display === "") {
      showMore.style.display = "none";
      showLess.style.display = "block";
    } else {
      showMore.style.display = "block";
      showLess.style.display = "none";
    }
  };

  const showApplyBtn = (min_subtotal) => {
    if (Number(min_subtotal || 0) > subtotal) {
      return false;
    } else {
      return true;
    }
  };

  const getDifferenceAmount = (min_subtotal) => {
    return Number(min_subtotal || 0) - subtotal;
  };

  /** True if this list coupon is currently applied (matches Redux elevate or vendor by code). */
  const isCouponApplied = (coupon) => {
    if (!coupon || !coupon.code) return false;
    if (appliedElevate && appliedElevate.code === coupon.code) return true;
    const rid = coupon.restaurant_id != null ? Number(coupon.restaurant_id) : null;
    if (rid != null && appliedVendors[rid] && appliedVendors[rid].code === coupon.code) return true;
    return false;
  };

  const handleRemoveCoupon = (coupon) => {
    if (!coupon || !coupon.code) return;
    if (appliedElevate && appliedElevate.code === coupon.code) {
      dispatch(removeOneCoupon("elevate"));
    } else {
      const rid = coupon.restaurant_id != null ? Number(coupon.restaurant_id) : null;
      if (rid != null) {
        dispatch(removeOneCoupon(rid));
      }
    }
    // Clear the pending coupon in Redux so Cart doesn't re-apply it on mount
    dispatch(clearPendingAppliedCoupon());
    // Clear stale coupon data so Cart doesn't re-apply the old coupon on mount
    try {
      sessionStorage.removeItem("cart_applied_coupon_payload");
      sessionStorage.removeItem("cart_return_from_coupon");
      localStorage.removeItem("appliedCoupon");
    } catch (e) { }
    history.push("/cart");
  };

  return (
    <div>
      <BackWithSearch
        boxshadow={true}
        has_title={true}
        title="Coupons"
        disable_search={true}
        homeButton={false}
        restaurant={{}}
      />
      <div
        className={`pt-60 ${classes["coupon-list"]}`}
      >
        <div className={classes["coupon-input-wrapper"]}>
          <input
            type="text"
            className={`form-control ${classes["coupon-list-input"]}`}
            placeholder="Enter Coupon Code"
            onChange={handleInput}
            spellCheck="false"
            value={enteredCoupon || ""}
          />
          <button
            className={`${classes["coupon-input-apply-btn"]} ${enteredCoupon && enteredCoupon.length ? classes["active"] : ""}`}
            style={{
              cursor: enteredCoupon && enteredCoupon.length ? "pointer" : "not-allowed"
            }}
            disabled={!enteredCoupon}
            onClick={(event) => {
              event.preventDefault();
              setAppliedCoupn();
            }}
          >
            APPLY
            <Ink duration="500" />
          </button>
        </div>

        {couponList && couponList.length ? (
          couponList.map((coupon, index) => {
            let currencySymbol = "";
            const discountType = (coupon.discount_type || coupon.type || "").toUpperCase();
            if (
              discountType === "PERCENTAGE" ||
              (coupon.max_discount && Number(coupon.max_discount) > 0) ||
              (coupon.description && coupon.description.indexOf("%") > -1)
            ) {
              currencySymbol = "%";
            }
            return (
              <div key={index} className={classes["coupon-card"]}>
                {/* Top Section: Logo, Code, Apply, Title */}
                <div className={classes["coupon-top-section"]}>
                  <div className={classes["coupon-header-row"]}>
                    <div className={classes["logo-code-wrapper"]}>
                      <img
                        className={classes["coupon-logo"]}
                        src="/assets/img/logos/coupon-icon.png"
                        alt="Icon"
                      />
                      <span
                        className={classes["coupon-code-box"]}
                        onClick={() => {
                          navigator.clipboard.writeText(coupon.code);
                          // Optional: You could show a toast here, but for now just copy
                        }}
                        style={{ cursor: "pointer" }}
                        title="Tap to copy"
                      >
                        {coupon.code}
                      </span>
                    </div>

                    {showApplyBtn(coupon.min_subtotal) && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isCouponApplied(coupon)) {
                            setCoupon(coupon);
                          } else {
                            handleRemoveCoupon(coupon);
                          }
                        }}
                        className={classes["coupon-apply-btn"]}
                        style={
                          isCouponApplied(coupon)
                            ? { color: "#ff4d4f", fontWeight: "600" }
                            : { color: "#ffa500" }
                        }
                      >
                        {isCouponApplied(coupon)
                          ? "REMOVE"
                          : (localStorage.getItem("applyCouponButtonText") || "APPLY")}
                      </button>
                    )}
                  </div>

                  <div className={classes["coupon-title"]}>
                    {coupon.name}
                  </div>

                  {coupon.description &&
                    <div className={classes["coupon-short-desc"]}>
                      {coupon.description}
                    </div>
                  }
                </div>

                {/* Divider with Cutouts */}
                <div className={classes["coupon-divider"]}>
                  <div className={classes["cutout-left"]}></div>
                  <div className={classes["dashed-line"]}></div>
                  <div className={classes["cutout-right"]}></div>
                </div>

                {/* Bottom Section: Footer/More */}
                <div className={classes["coupon-bottom-section"]}>

                  {!showApplyBtn(coupon.min_subtotal) && (
                    <div className={classes["offer-difference"]}>
                      Add{" "}
                      {localStorage.getItem("currencySymbolAlign") === "left" &&
                        localStorage.getItem("currencyFormat")}
                      {formatPrice(getDifferenceAmount(coupon.min_subtotal))}
                      {localStorage.getItem("currencySymbolAlign") === "right" &&
                        localStorage.getItem("currencyFormat")}{" "}
                      more to avail this offer
                    </div>
                  )}

                  <div className={classes["coupon-footer-text"]}>
                    {/* You can put additional brief info here if needed */}
                  </div>

                  <div
                    id={index}
                    onClick={(e) => {
                      e.preventDefault();
                      showMoreDetails(index, coupon.code);
                    }}
                  >
                    <span className={classes["show-more-less"]}>+ MORE</span>
                  </div>

                  <div
                    id={index + coupon.code}
                    className={classes["coupon-details-content"]}
                    style={{ display: "none" }}
                  >
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        showMoreDetails(index, coupon.code);
                      }}
                      style={{ marginBottom: '8px' }}
                    >
                      <span className={classes["show-more-less"]}>- LESS</span>
                    </div>
                    <ul>
                      {coupon.subtotal_message && (
                        <li>{coupon.subtotal_message}</li>
                      )}
                      {coupon.expiry_date && (
                        <li>
                          Expires: {getExpireDate(coupon.expiry_date)}
                        </li>
                      )}
                      {coupon.max_discount && (
                        <li>
                          Max discount:{" "}
                          {localStorage.getItem("currencySymbolAlign") === "left" &&
                            localStorage.getItem("currencyFormat")}
                          {formatPrice(Number(coupon.max_discount))}
                          {localStorage.getItem("currencySymbolAlign") ===
                            "right" && localStorage.getItem("currencyFormat")}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div>
            <p>Coupons not available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(CouponList);
